import React, {createContext, useMemo, useContext} from 'react'
import PropTypes from 'prop-types'
import {useTheme} from '@chakra-ui/react'
import useHydration from '../contexts/hydration'
import {CONTEXT_NOT_PROVIDED} from '../constants'

// This context provides media queries derived from Chakra UI breakpoints
// that can be used throughout the app for responsive behavior
const MediaQueriesContext = createContext(CONTEXT_NOT_PROVIDED)

const MediaQueriesProvider = ({children}) => {
    const isHydrated = useHydration()
    const {__breakpoints: {details: detailedBreakpoints} = {}} = useTheme()

    const mediaQueries = useMemo(() => {
        // Handle case where Chakra theme breakpoints aren't available (e.g., in tests)
        if (!detailedBreakpoints || !Array.isArray(detailedBreakpoints)) {
            return []
        }

        return detailedBreakpoints.map((bp) => {
            const {minMaxQuery, breakpoint} = bp
            const query = minMaxQuery.replace('@media screen and ', '')
            return {
                breakpoint,
                query: isHydrated ? window.matchMedia(query) : undefined // Fallback for SSR
            }
        })
    }, [isHydrated, detailedBreakpoints])

    // Expose media queries array directly (not wrapped in an object)
    return (
        <MediaQueriesContext.Provider value={mediaQueries}>{children}</MediaQueriesContext.Provider>
    )
}

const useMediaQueries = () => {
    const mediaQueries = useContext(MediaQueriesContext)
    if (mediaQueries === CONTEXT_NOT_PROVIDED) {
        throw new Error('useMediaQueries must be used within a MediaQueriesProvider')
    }
    return mediaQueries
}

MediaQueriesProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export default useMediaQueries
export {MediaQueriesProvider}
